import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import Popupz from "../../atoms/Popupz";
import Inputz from "../../atoms/Inputz";
import Buttonz from "../../atoms/Buttonz";
import { useDispatch } from "react-redux";
import { validateForgotEmail } from "../../../auth/validation";
import { validationPopup } from "../../../redux/actions";
import { getSignupRequestBaseUrl } from "../../../config";

function normalizeEmailInput(value) {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  return trimmed ? trimmed.toLowerCase() : "";
}

async function requestSignupLink(email) {
  const e = normalizeEmailInput(email);
  if (!e) return { ok: false, used: false, message: "Invalid email" };

  const base = getSignupRequestBaseUrl();
  const url = `${base}/signup-request`;

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email: e }),
  });

  let json = null;
  let rawText = "";
  try {
    rawText = await res.text();
    try {
      json = rawText ? JSON.parse(rawText) : null;
    } catch {
      json = null;
    }
  } catch {
    // ignore
  }

  const rawMsg = typeof json?.message === "string" ? json.message : "";
  const combined = `${rawMsg}\n${rawText || ""}`;
  const emailNotUnique =
    combined.includes("Value must be unique") ||
    combined.includes("email: Value must be unique") ||
    json?.data?.email?.code === "validation_not_unique" ||
    (typeof json?.data?.email?.message === "string" &&
      json.data.email.message.includes("unique"));

  // Some backends return 200 with ok:false even on validation failures.
  // Normalize that case so the UI shows a friendly message.
  if (res.ok && json?.ok === false && emailNotUnique) {
    return {
      ok: true,
      used: false,
      message: "Email already used for signup. Check your email to finish.",
      rid: null,
      token: null,
      expiresAt: null,
    };
  }

  if (!res.ok) {
    if (res.status === 400 && emailNotUnique) {
      return {
        ok: true,
        used: false,
        message: "Email already used for signup. Check your email to finish.",
        rid: null,
        token: null,
        expiresAt: null,
      };
    }
    return {
      ok: false,
      used: false,
      message: rawMsg || "Could not check email",
    };
  }

  return {
    ok: Boolean(json?.ok),
    used: Boolean(json?.used),
    message: typeof json?.message === "string" ? json.message : "",
    rid: typeof json?.rid === "string" ? json.rid : null,
    token: typeof json?.token === "string" ? json.token : null,
    expiresAt:
      typeof json?.expiresAt === "string" || json?.expiresAt instanceof Date
        ? json.expiresAt
        : null,
  };
}

function SignupPopup({ onClose }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [error, setError] = useState(false);
  const [stage, setStage] = useState("form"); // "form" | "sent"
  const [req, setReq] = useState({ rid: null, token: null, expiresAtMs: null });
  const [countdown, setCountdown] = useState(null);

  useEffect(() => {
    const rid = searchParams.get("rid");
    const token = searchParams.get("token");
    const isVerifiedSignupLink =
      location?.pathname === "/signup" && rid && token;
    if (isVerifiedSignupLink) {
      resetToForm();
      onClose?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.pathname, searchParams]);

  useEffect(() => {
    const onStorage = (ev) => {
      if (ev?.key === "uud:closeSignupPopup") {
        try {
          const v = localStorage.getItem("uud:closeSignupPopup");
          if (v) {
            resetToForm();
            onClose?.();
            localStorage.removeItem("uud:closeSignupPopup");
          }
        } catch {
          // ignore
        }
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (stage !== "sent" || !req.expiresAtMs) return undefined;
    const id = setInterval(() => {
      const diff = req.expiresAtMs - Date.now();
      if (diff <= 0) {
        setCountdown("Expired");
        clearInterval(id);
      } else {
        const minutes = Math.floor(diff / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        setCountdown(`${minutes}m ${seconds}s`);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [stage, req.expiresAtMs]);

  const resetToForm = () => {
    setStage("form");
    setReq({ rid: null, token: null, expiresAtMs: null });
    setCountdown(null);
    setEmail("");
    setError(false);
  };

  const abortSignupRequest = async () => {
    const base = getSignupRequestBaseUrl();
    if (!req.rid || !req.token) {
      resetToForm();
      return;
    }
    try {
      await fetch(`${base}/signup-request/cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rid: req.rid, token: req.token }),
      });
    } catch {
      // ignore
    }
    resetToForm();
  };

  const handleSubmit = async () => {
    const { errors: vErr } = validateForgotEmail(email);
    if (Object.keys(vErr).length > 0) {
      dispatch(
        validationPopup({
          message: vErr.email || "Email is required.",
          icon: "horror",
          type: "error",
        })
      );
      setError(true);
      return;
    }

    const normalized = normalizeEmailInput(email);

    dispatch(
      validationPopup({
        message: "Sending confirmation link...",
        icon: "loading",
        type: "loading",
      })
    );

    try {
      const result = await requestSignupLink(normalized);
      if (result.used) {
        dispatch(
          validationPopup({
            message: "Email already in use. Please login instead.",
            icon: "horror",
            type: "error",
          })
        );
        setError(true);
        return;
      }
      if (!result.ok) {
        dispatch(
          validationPopup({
            message: result.message || "Could not send confirmation link.",
            icon: "horror",
            type: "error",
          })
        );
        setError(true);
        return;
      }

      if (!result.rid || !result.token) {
        dispatch(
          validationPopup({
            message:
              result.message ||
              "Check your email. The link expires in 15 minutes.",
            icon: "like",
            type: "success",
          })
        );
        return;
      }

      dispatch(
        validationPopup({
          message: "Check your email. The link expires in 15 minutes.",
          icon: "like",
          type: "success",
        })
      );
      const expiresAtMs = result.expiresAt
        ? new Date(String(result.expiresAt)).getTime()
        : Date.now() + 15 * 60 * 1000;
      setReq({
        rid: result.rid,
        token: result.token,
        expiresAtMs: Number.isFinite(expiresAtMs) ? expiresAtMs : Date.now() + 15 * 60 * 1000,
      });
      setStage("sent");
    } catch (e) {
      dispatch(
        validationPopup({
          message:
            "Could not send confirmation link. Please try again in a moment.",
          icon: "horror",
          type: "error",
        })
      );
      setError(true);
    }
  };

  return (
    <Popupz
      title="Signup"
      text={
        <p>
          Welcome to UUD!
          <br />
          Enter your email, then continue to the full registration form.
        </p>
      }
      onClose={onClose}
      popupId="signup"
    >
      <div className="form-box">
        {stage === "form" ? (
          <>
            <Inputz
              iconL="mail"
              focusL="mail"
              label="Email"
              type="text"
              name="email"
              placeholder="Email"
              value={email}
              onChange={(e) => {
                setEmail(e.value);
                setError(false);
              }}
              error={error}
            />

            <Buttonz
              iconR="send"
              // hoverR="user"
              label="Continue to registration"
              onClick={handleSubmit}
            />
            <Buttonz label="Abort" onClick={onClose} className="secondary lined" />
          </>
        ) : (
          <>
            <div className="text-hint-box">
              We sent a confirmation link to <b>{email}</b>.
              <br />
              {countdown ? (
                <>
                  Time remaining: <b>{countdown}</b>
                </>
              ) : (
                <>Link expires in 15 minutes.</>
              )}
            </div>
            <Buttonz
              label="Abort"
              className="secondary lined"
              onClick={async () => {
                dispatch(
                  validationPopup({
                    message: "Cancelling…",
                    icon: "loading",
                    type: "loading",
                  })
                );
                await abortSignupRequest();
                dispatch(validationPopup(false));
              }}
            />
          </>
        )}
      </div>
      <div>
        Already member?{" "}
        <a
          href="#login"
          onClick={(e) => {
            e.preventDefault();
            onClose();
            dispatch({ type: "SHOW_POPUP", payload: "login" });
          }}
        >
          Login
        </a>
      </div>
    </Popupz>
  );
}

export default SignupPopup;
